import {
  Box,
  Menu,
  Fade,
  Table,
  Divider,
  MenuItem,
  Collapse,
  TableRow,
  TableBody,
  TableCell,
  TableHead,
  TextField,
  IconButton,
  Typography,
  Pagination,
  ListItemText,
  LinearProgress,
  InputAdornment,
  Tooltip,
} from "@mui/material";
import {
  KeyboardArrowDown,
  KeyboardArrowRight,
  ArrowDownward,
} from "@mui/icons-material";
import { Chip } from "../Chip";
import { Alert } from "../Alert";
import { Button } from "../Button";
import { debounce } from "lodash-es";
import StatusChip from "../StatusChip";
import { testIds } from "src/test/testIds";
import { css, keyframes } from "@emotion/react";
import { Search as SearchIcon } from "@mui/icons-material";
import React, { useState, useRef, useEffect } from "react";
import { COLOR_CONSTANTS, TYPOGRAPHY } from "../../constants/styles";
import {
  ProjectType,
  TIME_PROJECT,
} from "../../../../../src/domains/projects-list/type";
import { SelectedProject } from "src/domains/projects-list/project/ProjectPage";
import {
  matchQueryRenderState,
  type QueryRenderResult,
  type QueryRenderState,
} from "src/domains/shared/utils/queryRenderState";
import { QueryStateView } from "src/domains/shared/components/QueryStateView";
import {
  BRANCH_FADE_MS,
  fadeIn,
  REDUCED_MOTION_SX,
  SKELETON_SHOW_DELAY_MS,
  SkeletonBone,
  StateSlot,
} from "src/domains/shared/components/StateTransition";
import useLocale from "src/domains/shared/hooks/localeHook/useLocale";
import { useTimezone } from "src/domains/shared/hooks/useTimezone";
import {
  formatInstant,
  formatLocalDate,
} from "src/domains/shared/utils/datetime";
import { useTranslation } from "react-i18next";

// 프로젝트 목록 날짜 표기 포맷 (formatInstant/formatLocalDate 공통)
const PROJECT_DATE_FORMAT = "YYYY/MM/DD";

/**
 * 헤더 행(`TableHead` 의 arrow·title·timeSeriesCount·tags·permission·actions)의 열 수.
 * 행 전체를 가로지르는 셀(갈래 통지·펼침 상세)의 `colSpan` 이 여기서 나온다 — 열을 늘리면
 * 헤더와 함께 이 값을 고쳐야 그 셀들이 표 폭을 그대로 채운다.
 */
const PROJECT_TABLE_COLUMN_COUNT = 6;
/** 표 본문 한 행 높이 — 스켈레톤 행과 상태 칸 높이(한 페이지 행 수 × 이 값)가 여기서 나온다. */
const PROJECT_ROW_HEIGHT_PX = 64;
/** 검색 결과를 기다리는 동안 남겨 둔 행의 투명도. */
const REFRESHING_ROWS_OPACITY = 0.5;
const HEADER_ROW_HEIGHT_PX = 40;
const SKELETON_ROWS = [
  { title: "62%", tag: 40 },
  { title: "54%", tag: 52 },
  { title: "70%", tag: 36 },
  { title: "48%", tag: 48 },
  { title: "66%", tag: 40 },
  { title: "58%", tag: 56 },
];

export interface ProjectData extends ProjectType {
  permission: "Inspector" | "Owner";
  isExpanded?: boolean;
}

export type SortField = "title" | "permission" | "tags";
export type SortOrder = "asc" | "desc" | "none";

export interface SortConfig {
  field: SortField | null;
  order: SortOrder;
}

export interface ProjectTableMenuItem {
  id: string;
  label: string;
  disabled?: boolean;
  route?: string;
}

const AnimatedText: React.FC<{ text: string; style?: any }> = ({
  text,
  style,
}) => {
  const textRef = useRef<HTMLSpanElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [needsAnimation, setNeedsAnimation] = useState(false);
  const [needsTooltip, setNeedsTooltip] = useState(false);

  useEffect(() => {
    if (textRef.current && containerRef.current) {
      const containerWidth = containerRef.current.offsetWidth;
      const textWidth = textRef.current.scrollWidth;

      const isOverflowing = textWidth > containerWidth;
      setNeedsAnimation(isOverflowing);
      setNeedsTooltip(isOverflowing);
    }
  }, [text]);

  const content = (
    <Box
      ref={containerRef}
      sx={{
        position: "relative",
        overflow: "hidden",
        width: "100%",
        ...(needsAnimation && {
          "&:hover .animated-text": {
            animation: "flowText 5s linear infinite",
          },
        }),
      }}
    >
      <Typography
        ref={textRef}
        className={needsAnimation ? "animated-text" : ""}
        sx={{
          ...(style || styles.projectTitle),
          ...(needsAnimation && {
            "&:hover": {
              textOverflow: "unset",
              overflow: "visible",
              whiteSpace: "nowrap",
            },
          }),
        }}
      >
        {text}
      </Typography>
    </Box>
  );

  if (needsTooltip) {
    return (
      <Tooltip title={text} arrow placement="top">
        {content}
      </Tooltip>
    );
  }

  return content;
};

const AnimatedTags: React.FC<{ tags: string[] }> = ({ tags }) => {
  const tagsRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [needsAnimation, setNeedsAnimation] = useState(false);
  const [flowTagsAnimation, setFlowTagsAnimation] = useState<ReturnType<
    typeof keyframes
  > | null>(null);

  useEffect(() => {
    if (tagsRef.current && containerRef.current) {
      const containerWidth = containerRef.current.offsetWidth;
      const tagsWidth = tagsRef.current.scrollWidth;

      const needs = tagsWidth > containerWidth;
      setNeedsAnimation(needs);
      if (needs) {
        const distance = tagsWidth * 1.2 - containerWidth;
        setFlowTagsAnimation(
          keyframes`
            0% {
              transform: translateX(0);
            }
            100% {
              transform: translateX(-${distance}px);
            }
          `
        );
      }
    }
  }, [tags]);

  return (
    <Box
      ref={containerRef}
      sx={{
        position: "relative",
        overflow: "hidden",
        width: "100%",
      }}
    >
      <Box
        ref={tagsRef}
        className={needsAnimation ? "animated-tags" : ""}
        sx={{
          ...styles.tagsContainer,
          ...(needsAnimation &&
            flowTagsAnimation && {
              "&:hover": {
                animation: `${flowTagsAnimation} 5s linear infinite`,
              },
            }),
        }}
      >
        {tags.map((tag, index) => (
          <Chip key={index} appearance="tag" label={tag} size="small" />
        ))}
      </Box>
    </Box>
  );
};

const sortProjects = (
  projects: ProjectData[],
  sortConfig: SortConfig
): ProjectData[] => {
  if (!sortConfig.field || sortConfig.order === "none") {
    return projects;
  }

  return [...projects].sort((a, b) => {
    let aValue: string | number;
    let bValue: string | number;

    switch (sortConfig.field) {
      case "title":
        aValue = a.title.toLowerCase();
        bValue = b.title.toLowerCase();
        break;
      default:
        return 0;
    }

    if (aValue < bValue) {
      return sortConfig.order === "asc" ? -1 : 1;
    }
    if (aValue > bValue) {
      return sortConfig.order === "asc" ? 1 : -1;
    }
    return 0;
  });
};

/** 정렬된 목록에서 그 프로젝트가 들어 있는 쪽(1부터). 목록에 없으면 `undefined`. */
const findProjectPage = (
  sortedProjects: ProjectData[],
  projectId: number,
  countsPerPage: number
): number | undefined => {
  const index = sortedProjects.findIndex((project) => project.id === projectId);
  return index === -1 ? undefined : Math.floor(index / countsPerPage) + 1;
};

/** 표 바깥(지도 등)에서 고른 프로젝트를 표에 드러내 달라는 요청. */
export interface ProjectRevealRequest {
  projectId: number;
}

/**
 * 프로젝트 목록 테이블 props — 정렬/검색/페이지네이션/메뉴/펼치기 행 통합.
 * 행을 펼치면 해당 프로젝트의 timeseries 서브 행 목록이 나타난다.
 */
export interface ProjectTableProps {
  data: ProjectData[];
  /** 현재 펼쳐진 프로젝트의 선택된 timeseries 서브 행 */
  selectedSubRow: SelectedProject;
  onAddTimeSeries?: (project: ProjectData) => void;
  onDeleteSubItem?: (subItemId: string) => void;
  onPageChange?: (page: number) => void;
  onRowClick?: (project: ProjectData) => void;
  onSubRowClick?: (project: ProjectData, timeProject: TIME_PROJECT) => void;
  onMenuItemClick?: (
    projectId: string,
    timeProjectId: string,
    menuItem: ProjectTableMenuItem
  ) => void;
  menuItems?: ProjectTableMenuItem[];
  countsPerPage?: number;
  isLoading?: boolean;
  /** memo 컬럼 필터링용 키워드 (debounced) */
  searchKey?: string;
  /**
   * 바깥에서 고른 프로젝트가 있는 쪽으로 넘어가고 그 행을 스크롤 칸 안에 보인다.
   *
   * 요청마다 **새 객체**를 넘겨야 한다 — 표는 객체가 바뀐 것을 새 요청으로 읽으므로, 같은 프로젝트를
   * 다시 골라도 객체가 바뀌면 다시 넘어간다. 한 요청은 한 번만 처리해, 그 뒤 데이터·정렬이 바뀌어도
   * 다시 옮겨 가지 않는다. 쪽·정렬 상태는 계속 표 안에 있다.
   */
  revealRequest?: ProjectRevealRequest;
  /**
   * 표를 공급하는 쿼리의 렌더 갈래와 재시도 액션.
   *
   * **선택적**이다 — 넘기지 않는 소비처는 기존 2갈래(로딩 · 빈 상태)로 그대로 남고 동작이
   * 바뀌지 않는다. 넘기면 실패가 빈 상태("새 프로젝트를 생성하세요.")로 위장되지 않고
   * 오류 문구 + 재시도로 갈린다. 재시도를 갈래와 한 객체로 묶은 것은 의도적이다 —
   * 오류 갈래만 켜고 복구 수단을 빠뜨리는 조합이 타입에서 성립하지 않는다.
   */
  listQueryState?: {
    renderState: QueryRenderResult;
    onRetry: () => void;
    /** 새 검색어의 결과를 기다리는 동안 직전 행을 흐리게 남기고 머리 아래 진행 막대를 보인다. */
    isRefreshing?: boolean;
    /** 0건의 뜻 — 검색어·필터가 걸려 있으면 "검색 결과 없음", 아니면 "프로젝트 없음". */
    emptyKind?: ProjectListEmptyKind;
  };
}

export type ProjectListEmptyKind = "noProject" | "noResult";

export const ProjectTable: React.FC<ProjectTableProps> = ({
  data,
  selectedSubRow,
  onAddTimeSeries,
  onDeleteSubItem,
  onPageChange,
  onRowClick,
  onSubRowClick,
  onMenuItemClick,
  menuItems = [],
  countsPerPage = 10,
  isLoading = false,
  searchKey,
  revealRequest,
  listQueryState,
}) => {
  const { t } = useTranslation("projects");
  const [locale] = useLocale();
  const { timezone } = useTimezone();
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    field: null,
    order: "none",
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [menuAnchorEl, setMenuAnchorEl] = useState<{
    [key: string]: HTMLElement | null;
  }>({});
  const [currentMenuContext, setCurrentMenuContext] = useState<{
    projectId: string;
    timeProjectId: string;
  } | null>(null);
  const [memoFilters, setMemoFilters] = useState<Record<string, string>>({});
  const [debouncedMemoFilters, setDebouncedMemoFilters] = useState<
    Record<string, string>
  >({});

  const debouncedUpdateFiltersRef = useRef(
    debounce((filters: Record<string, string>) => {
      setDebouncedMemoFilters(filters);
    }, 300)
  );

  useEffect(() => {
    debouncedUpdateFiltersRef.current(memoFilters);
    return () => {
      debouncedUpdateFiltersRef.current.cancel();
    };
  }, [memoFilters]);

  const sortedData = sortProjects(data, sortConfig);

  // 새 요청은 렌더 중에 쪽으로 옮겨, 옛 쪽이 한 번 그려진 뒤 바뀌는 깜빡임이 없게 한다.
  const [handledRevealRequest, setHandledRevealRequest] =
    useState(revealRequest);
  if (revealRequest !== handledRevealRequest) {
    setHandledRevealRequest(revealRequest);
    const revealedPage = revealRequest
      ? findProjectPage(sortedData, revealRequest.projectId, countsPerPage)
      : undefined;
    if (revealedPage !== undefined) setCurrentPage(revealedPage);
  }

  const revealedRowRef = useRef<HTMLTableRowElement>(null);
  useEffect(() => {
    revealedRowRef.current?.scrollIntoView({ block: "nearest" });
  }, [revealRequest]);

  const totalPages = Math.max(1, Math.ceil(data.length / countsPerPage));
  const safePage = Math.min(currentPage, totalPages);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchKey]);

  const handleRowClick = (project: ProjectData) => {
    const projectId = project.id.toString();
    const newExpandedRows = new Set(expandedRows);

    if (newExpandedRows.has(projectId)) {
      newExpandedRows.delete(projectId);
    } else {
      newExpandedRows.add(projectId);
    }

    setExpandedRows(newExpandedRows);
    onRowClick?.(project);
  };

  const isRowExpanded = (projectId: string) => expandedRows.has(projectId);

  const handleSort = (field: SortField) => {
    setSortConfig((prev) => {
      if (prev.field === field) {
        switch (prev.order) {
          case "none":
            return { field, order: "asc" };
          case "asc":
            return { field, order: "desc" };
          case "desc":
            return { field: null, order: "none" };
          default:
            return { field, order: "asc" };
        }
      } else {
        return { field, order: "asc" };
      }
    });
  };

  const getSortIcon = (field: SortField) => {
    if (sortConfig.field !== field) {
      return <ArrowDownward sx={{ fontSize: 16, opacity: 0.3 }} />;
    }

    const iconStyle = { fontSize: 16, opacity: 1 };

    switch (sortConfig.order) {
      case "asc":
        return <ArrowDownward sx={iconStyle} />;
      case "desc":
        return (
          <ArrowDownward sx={{ ...iconStyle, transform: "rotate(180deg)" }} />
        );
      default:
        return <ArrowDownward sx={{ fontSize: 16, opacity: 0.3 }} />;
    }
  };

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    page: number
  ) => {
    setCurrentPage(page);
    onPageChange?.(page);
  };

  const handleSubRowClick = (project: ProjectData, timeProject: any) => {
    onSubRowClick?.(project, timeProject);
  };

  const handleMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    projectId: string,
    timeProjectId: string
  ) => {
    event.stopPropagation();
    const menuKey = `${projectId}-${timeProjectId}`;
    setMenuAnchorEl((prev) => ({
      ...prev,
      [menuKey]: event.currentTarget,
    }));
    setCurrentMenuContext({ projectId, timeProjectId });
  };

  const handleMenuClose = (projectId: string, timeProjectId: string) => {
    const menuKey = `${projectId}-${timeProjectId}`;
    setMenuAnchorEl((prev) => ({
      ...prev,
      [menuKey]: null,
    }));
    setCurrentMenuContext(null);
  };

  const handleMenuItemClick = (menuItem: ProjectTableMenuItem) => {
    if (currentMenuContext && !menuItem.disabled) {
      onMenuItemClick?.(
        currentMenuContext.projectId,
        currentMenuContext.timeProjectId,
        menuItem
      );
    }

    if (currentMenuContext) {
      handleMenuClose(
        currentMenuContext.projectId,
        currentMenuContext.timeProjectId
      );
    }
  };

  const getProjectId = (project: ProjectData) => project.id.toString();

  const normalizeSearchText = (text: string) =>
    text.toLowerCase().replace(/\s+/g, "");

  const matchesMemoFilter = (
    comment: string | undefined,
    filter: string
  ): boolean => {
    if (!filter.trim()) return true;
    const normalizedComment = normalizeSearchText(comment || "");
    const normalizedFilter = normalizeSearchText(filter);
    return normalizedComment.includes(normalizedFilter);
  };

  const filterProjectsByMemo = (
    timeProjects: any[] | undefined,
    memoFilter: string
  ) => {
    if (!timeProjects) return [];
    return timeProjects.filter((timeProject) =>
      matchesMemoFilter(timeProject.comment, memoFilter)
    );
  };

  const getFilteredMemoProjects = (project: ProjectData) => {
    const projectId = getProjectId(project);
    const memoFilter = debouncedMemoFilters[projectId] || "";
    return filterProjectsByMemo(project.time_projects, memoFilter);
  };

  const hasNoMemoSearchResults = (project: ProjectData) => {
    const projectId = getProjectId(project);
    const memoFilter = debouncedMemoFilters[projectId] || "";
    const hasActiveFilter = memoFilter.trim().length > 0;
    if (!hasActiveFilter) return false;

    const filteredProjects = getFilteredMemoProjects(project);
    return filteredProjects.length === 0;
  };

  const startIndex = (safePage - 1) * countsPerPage;
  const endIndex = startIndex + countsPerPage;
  const paginatedData = sortedData.slice(startIndex, endIndex);

  // 실패 상태 입력을 받지 못한 소비처의 갈래 — 조회 실패를 알 방법이 없으므로 로딩과
  // 빈 상태 둘로만 갈린다(기존 동작 그대로). 실패를 구별하려면 `listQueryState` 를 넘긴다.
  const fallbackRenderState = (): QueryRenderState => {
    if (isLoading) return "pending";
    return paginatedData.length === 0 ? "empty" : "ready";
  };

  const renderState: QueryRenderResult = listQueryState?.renderState ?? {
    state: fallbackRenderState(),
    degraded: false,
  };

  const loadFailedAlert = (
    <Alert
      type="error"
      message={t("queryState.projectListLoadFailed", { ns: "projects" })}
      actionLabel={t("queryState.retry", { ns: "common" })}
      onActionClick={() => listQueryState?.onRetry()}
    />
  );

  const isRefreshing = listQueryState?.isRefreshing ?? false;
  const emptyKind = listQueryState?.emptyKind ?? "noProject";
  const refreshingSx = {
    opacity: isRefreshing ? REFRESHING_ROWS_OPACITY : 1,
    transition: "opacity 150ms ease",
    ...REDUCED_MOTION_SX,
  };
  // 실패·0건 상태 칸은 표 행이 아니라 표 머리 아래 남은 자리 전체를 채운다 — 스크롤 칸이 한 페이지 행 높이보다
  // 크면 그 자리 한가운데 서고, 작아도 한 페이지 행 높이 아래로는 줄지 않아 행이 있을 때와 틀이 같다.
  const stateAreaMinHeight = countsPerPage * PROJECT_ROW_HEIGHT_PX;
  // 실패 갈래에서 빈 상태 문구("새 프로젝트를 생성하세요.")를 띄우면 사용자는
  // 이미 가진 프로젝트를 또 만든다 — 여기엔 생성 유도가 있어서는 안 된다.
  const failedSlot = (
    <StateSlot
      tone="error"
      icon="alert"
      title={t("queryState.projectListLoadFailed", { ns: "projects" })}
      retryLabel={t("queryState.retry", { ns: "common" })}
      onRetry={() => listQueryState?.onRetry()}
    />
  );
  // 검색어·태그 필터가 걸린 0건에서 생성을 권하면 사용자는 이미 가진 프로젝트를 또 만든다.
  const emptySlot =
    emptyKind === "noResult" ? (
      <StateSlot tone="neutral" icon="search" title={t("table.noSearchResult", { ns: "projects" })} />
    ) : (
      <StateSlot
        tone="info"
        icon="folder"
        title={t("dashboard.noProjects", { ns: "projects" })}
        description={t("table.createNewProject", { ns: "projects" })}
      />
    );
  const stateSlot = matchQueryRenderState(renderState.state, {
    pending: () => null,
    error: () => failedSlot,
    empty: () => emptySlot,
    ready: () => null,
  });

  return (
    <Box sx={styles.container}>
      {isRefreshing && (
        <LinearProgress
          aria-label={t("table.searching", { ns: "projects" })}
          sx={styles.refreshBar}
        />
      )}
      <Box sx={styles.scrollContainer}>
        <Table sx={styles.table}>
          <TableHead sx={styles.stickyHeader}>
            <TableRow sx={styles.headerRow}>
              <TableCell
                sx={{
                  ...styles.columnWidths.arrow,
                  paddingTop: "6px",
                  paddingBottom: "6px",
                }}
              ></TableCell>
              <TableCell sx={styles.columnWidths.title}>
                <Box
                  sx={styles.sortableHeader}
                  onClick={() => handleSort("title")}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      handleSort("title");
                    }
                  }}
                  aria-label={t("table.columns.name", { ns: "projects" })}
                >
                  <Typography sx={styles.headerText}>
                    {t("table.columns.name", { ns: "projects" })}
                  </Typography>
                  {getSortIcon("title")}
                </Box>
              </TableCell>
              <TableCell sx={styles.columnWidths.timeSeriesCount}>
                <Typography sx={styles.headerCell}>
                  {t("table.columns.timeSeriesCount", { ns: "projects" })}
                </Typography>
              </TableCell>
              <TableCell sx={styles.columnWidths.tags}>
                <Typography sx={styles.headerCell}>
                  {t("modal.tags", { ns: "projects" })}
                </Typography>
              </TableCell>
              <TableCell sx={styles.columnWidths.permission}>
                <Typography sx={styles.headerCell}>
                  {t("myPage.permission.permission", { ns: "projects" })}
                </Typography>
              </TableCell>
              <TableCell sx={styles.columnWidths.actions}>
                <Typography sx={styles.headerCell}>
                  {t("table.columns.actions", { ns: "projects" })}
                </Typography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody
            aria-busy={isRefreshing || renderState.state === "pending"}
            sx={refreshingSx}
          >
            {/*
              저하 통지는 `QueryStateView` 가 갈래 매핑 **바깥**에 놓는다 — `empty`("프로젝트
              0개") 와 `ready` 두 갈래 위에 모두 얹혀야 한다. 표 안이라 통지도 행 단위여야
              `<TableBody>` 의 자식 계약이 깨지지 않는다.
            */}
            <QueryStateView
              renderState={renderState}
              degradedNotice={
                <TableRow>
                  <TableCell
                    colSpan={PROJECT_TABLE_COLUMN_COUNT}
                    sx={{ border: "none" }}
                  >
                    {loadFailedAlert}
                  </TableCell>
                </TableRow>
              }
              branches={{
                pending: () => (
                  <>
                    {SKELETON_ROWS.map((row, index) => (
                      <TableRow key={row.title} css={skeletonRowCss} sx={{ height: PROJECT_ROW_HEIGHT_PX }}>
                        <TableCell sx={styles.skeletonCell}>
                          {index === 0 && (
                            <Box component="span" role="status" sx={styles.visuallyHidden}>
                              {t("table.listLoading", { ns: "projects" })}
                            </Box>
                          )}
                        </TableCell>
                        <TableCell sx={styles.skeletonCell}>
                          <Box sx={{ display: "flex", flexDirection: "column", gap: "6px", paddingRight: "8px" }}>
                            <SkeletonBone width={row.title} height={12} />
                            <SkeletonBone width="46%" height={10} />
                          </Box>
                        </TableCell>
                        <TableCell sx={styles.skeletonCell}>
                          <SkeletonBone width={28} height={24} radius={12} />
                        </TableCell>
                        <TableCell sx={styles.skeletonCell}>
                          <Box sx={{ display: "flex", gap: "4px" }}>
                            <SkeletonBone width={row.tag} height={22} radius={11} />
                            <SkeletonBone width={44} height={22} radius={11} />
                          </Box>
                        </TableCell>
                        <TableCell sx={styles.skeletonCell}>
                          <SkeletonBone width={56} height={12} />
                        </TableCell>
                        <TableCell sx={styles.skeletonCell}>
                          <SkeletonBone width={92} height={36} />
                        </TableCell>
                      </TableRow>
                    ))}
                  </>
                ),
                // 실패·0건은 표 아래 상태 칸(`stateSlot`)이 그린다 — 표 본문에는 행이 없다.
                error: () => null,
                empty: () => null,
                ready: () => (
                  <>
                    {paginatedData.map((project) => (
                      <React.Fragment key={project.id}>
                        <TableRow
                          hover
                          ref={
                            project.id === revealRequest?.projectId
                              ? revealedRowRef
                              : undefined
                          }
                          data-testid={testIds.project.row(project.id)}
                          onClick={() => handleRowClick(project)}
                          sx={{
                            ...styles.mainRow,
                            backgroundColor:
                              selectedSubRow.projectId === project.id
                                ? styles.selectedSubRow.backgroundColor
                                : "inherit",
                            ...styles.tableRow,
                          }}
                        >
                          <TableCell
                            sx={{
                              ...styles.arrowCell,
                              ...styles.columnWidths.arrow,
                            }}
                          >
                            <IconButton
                              size="small"
                              data-testid={testIds.project.expandBtn(
                                project.id
                              )}
                              aria-label={project.title}
                              aria-expanded={isRowExpanded(
                                project.id.toString()
                              )}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRowClick(project);
                              }}
                            >
                              {isRowExpanded(project.id.toString()) ? (
                                <KeyboardArrowDown fontSize="small" />
                              ) : (
                                <KeyboardArrowRight fontSize="small" />
                              )}
                            </IconButton>
                          </TableCell>
                          <TableCell sx={styles.columnWidths.title}>
                            <Box sx={styles.projectInfo}>
                              <Box sx={styles.projectDetails}>
                                <AnimatedText text={project.title} />
                                <Typography sx={styles.projectDate}>
                                  {t("table.lastModified", { ns: "projects" })}:{" "}
                                  {formatInstant(
                                    project.updated || project.created,
                                    PROJECT_DATE_FORMAT,
                                    { tz: timezone }
                                  )}
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell sx={styles.columnWidths.timeSeriesCount}>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: "4px",
                              }}
                            >
                              <Chip
                                appearance="tag"
                                label={`${project.time_projects?.length || 0}`}
                                size="small"
                              />
                            </Box>
                          </TableCell>
                          <TableCell sx={styles.columnWidths.tags}>
                            <AnimatedTags tags={project.tags} />
                          </TableCell>
                          <TableCell sx={styles.columnWidths.permission}>
                            <Typography sx={styles.permissionText}>
                              {project.permission}
                            </Typography>
                          </TableCell>
                          <TableCell sx={styles.columnWidths.actions}>
                            <Button
                              size="medium"
                              emphasis="outlined"
                              intent="primary"
                              sx={styles.addTimeSeriesButton}
                              onClick={(e) => {
                                e.stopPropagation();
                                onAddTimeSeries?.(project);
                              }}
                            >
                              {t("table.addTimeSeries", { ns: "projects" })}
                            </Button>
                          </TableCell>
                        </TableRow>

                        <TableRow>
                          <TableCell
                            colSpan={PROJECT_TABLE_COLUMN_COUNT}
                            sx={styles.collapseCell}
                          >
                            <Collapse
                              in={isRowExpanded(project.id.toString())}
                              timeout="auto"
                            >
                              <Box sx={styles.collapseContent}>
                                <Table
                                  sx={{
                                    tableLayout: "fixed",
                                    width: "100%",
                                    "& .MuiTableCell-root": { padding: "8px" },
                                  }}
                                >
                                  <TableHead>
                                    <TableRow sx={styles.subTableHeaderRow}>
                                      <TableCell
                                        sx={{
                                          width: "200px",
                                          color: COLOR_CONSTANTS.text.disabled,
                                          ...TYPOGRAPHY.scale.caption2,
                                        }}
                                      >
                                        {t("table.columns.date", {
                                          ns: "projects",
                                        })}
                                      </TableCell>
                                      <TableCell sx={styles.memoSearchCell}>
                                        <Box sx={styles.memoSearchContainer}>
                                          <Typography
                                            sx={styles.memoSearchLabel}
                                          >
                                            {t("table.columns.memo", {
                                              ns: "projects",
                                            })}
                                          </Typography>
                                          <TextField
                                            size="small"
                                            inputProps={{
                                              "data-testid":
                                                testIds.project.memoSearchInput,
                                            }}
                                            placeholder={t("table.memoSearch", {
                                              ns: "projects",
                                            })}
                                            value={
                                              memoFilters[
                                                getProjectId(project)
                                              ] || ""
                                            }
                                            onChange={(e) => {
                                              const projectId =
                                                getProjectId(project);
                                              setMemoFilters((prev) => ({
                                                ...prev,
                                                [projectId]: e.target.value,
                                              }));
                                            }}
                                            onClick={(e) => e.stopPropagation()}
                                            onKeyDown={(e) =>
                                              e.stopPropagation()
                                            }
                                            sx={styles.memoSearchInput}
                                            InputProps={{
                                              endAdornment: (
                                                <InputAdornment
                                                  position="end"
                                                  sx={
                                                    styles.memoSearchAdornment
                                                  }
                                                >
                                                  <SearchIcon
                                                    sx={styles.memoSearchIcon}
                                                  />
                                                </InputAdornment>
                                              ),
                                            }}
                                          />
                                        </Box>
                                      </TableCell>
                                      <TableCell
                                        sx={{
                                          width: "160px",
                                          minWidth: "160px",
                                          color: COLOR_CONSTANTS.text.disabled,
                                          ...TYPOGRAPHY.scale.caption2,
                                        }}
                                      >
                                        {t("table.columns.mappingStatus", {
                                          ns: "projects",
                                        })}
                                      </TableCell>
                                      <TableCell
                                        sx={{
                                          width: "120px",
                                          color: COLOR_CONSTANTS.text.disabled,
                                          ...TYPOGRAPHY.scale.caption2,
                                        }}
                                      >
                                        {t("table.columns.actions", {
                                          ns: "projects",
                                        })}
                                      </TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {hasNoMemoSearchResults(project) ? (
                                      <TableRow>
                                        <TableCell
                                          colSpan={4}
                                          sx={styles.emptySearchResult}
                                        >
                                          {t("table.noMemoResults", {
                                            ns: "projects",
                                          })}
                                        </TableCell>
                                      </TableRow>
                                    ) : (
                                      getFilteredMemoProjects(project).map(
                                        (timeProject: TIME_PROJECT) => (
                                          <Fade
                                            key={timeProject.id}
                                            in={true}
                                            timeout={200}
                                            unmountOnExit
                                          >
                                            <TableRow
                                              data-testid={testIds.ts.row(
                                                timeProject.id
                                              )}
                                              onClick={() =>
                                                handleSubRowClick(
                                                  project,
                                                  timeProject
                                                )
                                              }
                                              sx={{
                                                ...styles.subRow,
                                                backgroundColor:
                                                  selectedSubRow.projectId ===
                                                    project.id &&
                                                  selectedSubRow.timeSeriesId ===
                                                    timeProject.id
                                                    ? styles.selectedSubRow
                                                        .backgroundColor
                                                    : "inherit",
                                                ...styles.tableRow,
                                              }}
                                            >
                                              <TableCell
                                                sx={{
                                                  width: "200px",
                                                }}
                                              >
                                                <Box
                                                  sx={styles.subRowContent}
                                                  role="button"
                                                  tabIndex={0}
                                                  aria-pressed={
                                                    selectedSubRow.projectId ===
                                                      project.id &&
                                                    selectedSubRow.timeSeriesId ===
                                                      timeProject.id
                                                  }
                                                  aria-label={`${formatLocalDate(
                                                    timeProject.timestamp,
                                                    PROJECT_DATE_FORMAT
                                                  )}`}
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleSubRowClick(
                                                      project,
                                                      timeProject
                                                    );
                                                  }}
                                                  onKeyDown={(e) => {
                                                    if (
                                                      e.key === "Enter" ||
                                                      e.key === " "
                                                    ) {
                                                      e.preventDefault();
                                                      handleSubRowClick(
                                                        project,
                                                        timeProject
                                                      );
                                                    }
                                                  }}
                                                >
                                                  <Box
                                                    sx={styles.subRowDetails}
                                                  >
                                                    <Typography
                                                      sx={styles.subRowTitle}
                                                    >
                                                      {formatLocalDate(
                                                        timeProject.timestamp,
                                                        PROJECT_DATE_FORMAT
                                                      )}
                                                    </Typography>
                                                    <Typography
                                                      sx={styles.subRowDate}
                                                    >
                                                      {t("table.lastModified", {
                                                        ns: "projects",
                                                      })}
                                                      :{" "}
                                                      {formatInstant(
                                                        timeProject.updated,
                                                        PROJECT_DATE_FORMAT,
                                                        { tz: timezone }
                                                      )}
                                                    </Typography>
                                                  </Box>
                                                </Box>
                                              </TableCell>
                                              <TableCell sx={{ width: "40%" }}>
                                                <Box sx={styles.subRowMemo}>
                                                  <Box
                                                    component="img"
                                                    src="/icons/16/etc/file-list-line.svg"
                                                    alt="file-list"
                                                    sx={styles.subRowIcon}
                                                  />
                                                  <AnimatedText
                                                    text={
                                                      timeProject.comment || "-"
                                                    }
                                                    style={
                                                      styles.subRowMemoText
                                                    }
                                                  />
                                                </Box>
                                              </TableCell>
                                              <TableCell
                                                sx={{ width: "160px" }}
                                              >
                                                <Box sx={styles.subRowStatus}>
                                                  <Box
                                                    sx={styles.subRowStatusInfo}
                                                  >
                                                    <Box
                                                      component="img"
                                                      src="/icons/16/etc/file-image-line.svg"
                                                      alt="file-image"
                                                      sx={
                                                        styles.subRowStatusIcon
                                                      }
                                                    />
                                                    <Typography
                                                      sx={
                                                        styles.subRowStatusText
                                                      }
                                                    >
                                                      {timeProject.image_count ||
                                                        "0"}
                                                    </Typography>
                                                  </Box>
                                                  <Divider
                                                    orientation="vertical"
                                                    flexItem
                                                  />
                                                  <Box
                                                    sx={styles.subRowStatusChip}
                                                  >
                                                    <StatusChip
                                                      status={
                                                        timeProject.current_state
                                                      }
                                                    />
                                                  </Box>
                                                </Box>
                                              </TableCell>
                                              <TableCell
                                                sx={{ width: "120px" }}
                                              >
                                                <Box sx={styles.subRowActions}>
                                                  <Tooltip
                                                    title="2D3D"
                                                    placement="top"
                                                  >
                                                    <IconButton
                                                      size="small"
                                                      data-testid={testIds.ts.menuBtn(
                                                        timeProject.id
                                                      )}
                                                      aria-label={`2D3D - ${formatLocalDate(
                                                        timeProject.timestamp,
                                                        PROJECT_DATE_FORMAT
                                                      )}`}
                                                      onClick={(event) => {
                                                        if (
                                                          selectedSubRow?.timeSeriesId !==
                                                          timeProject.id
                                                        ) {
                                                          onSubRowClick?.(
                                                            project,
                                                            timeProject
                                                          );
                                                        }
                                                        handleMenuOpen(
                                                          event,
                                                          project.id.toString(),
                                                          timeProject.id.toString()
                                                        );
                                                      }}
                                                    >
                                                      <Box
                                                        component="img"
                                                        src="/icons/20/etc/nav.svg"
                                                        alt=""
                                                        sx={
                                                          styles.subRowActionIcon
                                                        }
                                                      />
                                                    </IconButton>
                                                  </Tooltip>
                                                  <Menu
                                                    anchorEl={
                                                      menuAnchorEl[
                                                        `${project.id}-${timeProject.id}`
                                                      ]
                                                    }
                                                    open={Boolean(
                                                      menuAnchorEl[
                                                        `${project.id}-${timeProject.id}`
                                                      ]
                                                    )}
                                                    onClose={() =>
                                                      handleMenuClose(
                                                        project.id.toString(),
                                                        timeProject.id.toString()
                                                      )
                                                    }
                                                    anchorOrigin={{
                                                      vertical: "bottom",
                                                      horizontal: "right",
                                                    }}
                                                    transformOrigin={{
                                                      vertical: "top",
                                                      horizontal: "right",
                                                    }}
                                                    slotProps={{
                                                      list: {
                                                        sx: styles.menu.list,
                                                      },
                                                      paper: {
                                                        sx: styles.menu.paper,
                                                      },
                                                      backdrop: {
                                                        onClick: (e) =>
                                                          e.stopPropagation(),
                                                      },
                                                    }}
                                                  >
                                                    {menuItems.map(
                                                      (menuItem) => (
                                                        <MenuItem
                                                          key={menuItem.id}
                                                          onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleMenuItemClick(
                                                              menuItem
                                                            );
                                                          }}
                                                          disabled={
                                                            menuItem.disabled
                                                          }
                                                          sx={styles.menuItem}
                                                        >
                                                          <ListItemText
                                                            primary={
                                                              menuItem.label
                                                            }
                                                            sx={
                                                              menuItem.disabled
                                                                ? styles.menuItemTextDisabled
                                                                : styles.menuItemText
                                                            }
                                                            primaryTypographyProps={{
                                                              ...TYPOGRAPHY
                                                                .scale
                                                                .buttonMedium,
                                                            }}
                                                          />
                                                        </MenuItem>
                                                      )
                                                    )}
                                                  </Menu>
                                                  <Divider
                                                    orientation="vertical"
                                                    flexItem
                                                  />
                                                  <Tooltip
                                                    title="delete"
                                                    placement="top"
                                                  >
                                                    <IconButton
                                                      size="small"
                                                      data-testid={testIds.ts.deleteBtn(
                                                        timeProject.id
                                                      )}
                                                      aria-label={`${t(
                                                        "myPage.permission.actions.delete",
                                                        { ns: "projects" }
                                                      )} - ${formatLocalDate(
                                                        timeProject.timestamp,
                                                        PROJECT_DATE_FORMAT
                                                      )}`}
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        if (
                                                          selectedSubRow?.timeSeriesId !==
                                                          timeProject.id
                                                        ) {
                                                          onSubRowClick?.(
                                                            project,
                                                            timeProject
                                                          );
                                                        }
                                                        onDeleteSubItem?.(
                                                          timeProject.id.toString()
                                                        );
                                                      }}
                                                    >
                                                      <Box
                                                        component="img"
                                                        src="/icons/20/etc/trash.svg"
                                                        alt=""
                                                        sx={
                                                          styles.subRowActionIcon
                                                        }
                                                      />
                                                    </IconButton>
                                                  </Tooltip>
                                                </Box>
                                              </TableCell>
                                            </TableRow>
                                          </Fade>
                                        )
                                      )
                                    )}
                                  </TableBody>
                                </Table>
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    ))}
                  </>
                ),
              }}
            />
          </TableBody>
        </Table>
        {stateSlot && (
          <Box sx={{ ...styles.stateArea, ...refreshingSx, minHeight: stateAreaMinHeight }}>{stateSlot}</Box>
        )}
      </Box>

      <Box sx={styles.paginationContainer}>
        <Pagination
          count={totalPages}
          page={safePage}
          onChange={handlePageChange}
          color="primary"
          showFirstButton
          showLastButton
        />
      </Box>
    </Box>
  );
};

const skeletonRowCss = css`
  animation: ${fadeIn} ${BRANCH_FADE_MS}ms ease ${SKELETON_SHOW_DELAY_MS}ms both;
  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const styles = {
  container: {
    position: "relative",
    flex: 1,
    borderRadius: "8px",
    border: `1px solid ${COLOR_CONSTANTS.divider.divider}`,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  fixedHeader: {
    flexShrink: 0,
    borderBottom: `1px solid ${COLOR_CONSTANTS.divider.divider}`,
  },
  refreshBar: {
    position: "absolute",
    top: HEADER_ROW_HEIGHT_PX,
    left: 0,
    right: 0,
    height: 2,
    zIndex: 2,
  },
  stateArea: {
    flex: 1,
    display: "flex",
  },
  skeletonCell: {
    borderBottom: `1px solid ${COLOR_CONSTANTS.divider.divider}`,
  },
  visuallyHidden: {
    position: "absolute",
    width: 1,
    height: 1,
    overflow: "hidden",
    clip: "rect(0 0 0 0)",
    whiteSpace: "nowrap",
  },
  scrollContainer: {
    width: "100%",
    maxWidth: "100%",
    flex: 1,
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
    overflow: "auto",
    scrollbarGutter: "stable",
    "&::-webkit-scrollbar": {
      height: "4px",
      width: "0px",
    },
    "&::-webkit-scrollbar-track": {
      backgroundColor: "transparent",
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: "rgba(0, 0, 0, 0.2)",
      borderRadius: "2px",
    },
    "&::-webkit-scrollbar-thumb:hover": {
      backgroundColor: "rgba(0, 0, 0, 0.3)",
    },
    "&::-moz-scrollbar": {
      width: "0px",
      height: "4px",
    },
  },
  tableHead: {
    backgroundColor: COLOR_CONSTANTS.COLOR.GREY[100],
  },
  stickyHeader: {
    backgroundColor: COLOR_CONSTANTS.COLOR.GREY[100],
  },
  table: {
    flexShrink: 0,
    tableLayout: "fixed",
    minWidth: "600px",
    "& .MuiTableCell-root:nth-child(even)": { padding: "8px" },
  },
  columnWidths: {
    arrow: { width: "40px", minWidth: "40px" },
    title: { width: "25%", minWidth: "25%" },
    tags: { width: "25%", minWidth: "25%" },
    timeSeriesCount: { width: "10%", minWidth: "10%" },
    permission: { width: "20%", minWidth: "20%" },
    actions: { width: "20%", minWidth: "20%" },
  },
  headerRow: {
    height: "40px",
    "& > *": {
      padding: "8px 0px",
      paddingTop: "0",
      paddingBottom: "0",
      color: COLOR_CONSTANTS.text.disabled,
      borderBottom: `1px solid ${COLOR_CONSTANTS.divider.divider}`,
    },
  },
  sortableHeader: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    cursor: "pointer",
  },
  headerText: {
    ...TYPOGRAPHY.scale.tableHeader,
    color: "rgba(0, 0, 0, 0.38)",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  headerCell: {
    ...TYPOGRAPHY.scale.tableHeader,
    color: "rgba(0, 0, 0, 0.38)",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  tableRow: {
    "& > *": {
      borderBottom: `1px solid ${COLOR_CONSTANTS.divider.divider}`,
      borderTop: `1px solid ${COLOR_CONSTANTS.divider.divider}`,
    },
  },
  mainRow: {
    cursor: "pointer",
    borderBottom: `1px solid ${COLOR_CONSTANTS.divider.divider}`,
    borderTop: `1px solid ${COLOR_CONSTANTS.divider.divider}`,
  },
  expandedRow: {
    backgroundColor: "#fafafa",
  },
  arrowCell: {
    padding: "0",
  },
  projectInfo: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  projectDetails: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    overflow: "hidden",
    width: "100%",
  },
  projectTitle: {
    ...TYPOGRAPHY.scale.subtitle1,
    color: COLOR_CONSTANTS.text.primary,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    "@keyframes flowText": {
      "0%": {
        transform: "translateX(0)",
      },
      "100%": {
        transform: "translateX(-100%)",
      },
    },
  },
  projectDate: {
    ...TYPOGRAPHY.scale.body2,
    color: COLOR_CONSTANTS.text.disabled,
    overflow: "hidden",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
  },
  tagsContainer: {
    display: "flex",
    flexWrap: "nowrap",
    gap: "4px",
    alignItems: "center",
    whiteSpace: "nowrap",
  },
  permissionText: {
    ...TYPOGRAPHY.scale.body2,
    color: "rgba(0, 0, 0, 0.6)",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  collapseCell: {
    padding: "0",
  },
  collapseContent: {
    paddingLeft: "48px",
    backgroundColor: "#fafafa",
  },
  subTableHeaderRow: {
    "& > *": {
      paddingTop: "6px",
      paddingBottom: "6px",
      textWrap: "nowrap",

      borderBottom: `1px solid ${COLOR_CONSTANTS.divider.divider}`,
    },
  },
  subRow: {
    cursor: "pointer",
    borderBottom: `1px solid ${COLOR_CONSTANTS.divider.divider}`,
    borderTop: `1px solid ${COLOR_CONSTANTS.divider.divider}`,
  },
  selectedSubRow: {
    backgroundColor: "rgba(29, 116, 255, 0.08)",
  },
  subRowContent: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  subRowDetails: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },
  subRowTitle: {
    ...TYPOGRAPHY.scale.subtitle1,
    color: COLOR_CONSTANTS.text.primary,
  },
  subRowDate: {
    ...TYPOGRAPHY.scale.body2,
    color: COLOR_CONSTANTS.text.disabled,
  },
  subRowMemo: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    width: "100%",
    overflow: "hidden",
    minWidth: 0,
  },
  subRowIcon: {
    width: "16px",
    height: "16px",
  },
  subRowMemoText: {
    ...TYPOGRAPHY.scale.body2,
    color: "rgba(0, 0, 0, 0.6)",
    flex: 1,
    minWidth: 0,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  subRowStatus: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  subRowStatusInfo: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  subRowStatusIcon: {
    width: "16px",
    height: "16px",
  },
  subRowStatusText: {
    ...TYPOGRAPHY.scale.body2,
    minWidth: "35px",
    color: "rgba(0, 0, 0, 0.6)",
  },
  subRowStatusChip: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
    borderRadius: "1000px",
    height: "22px",
  },
  subRowActions: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  subRowActionIcon: {
    width: "20px",
    height: "20px",
  },
  addTimeSeriesButton: {
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    fontSize: "12px",
    padding: "4px 8px",
    minWidth: "auto",
  },
  paginationContainer: {
    flexShrink: 0,
    display: "flex",
    justifyContent: "center",
    padding: "24px 0",
    backgroundColor: "#fafafa",
    borderTop: `1px solid ${COLOR_CONSTANTS.divider.divider}`,
  },
  menu: {
    list: { padding: "0" },
    paper: {
      display: "flex",
      flexDirection: "column",
      gap: "6px",
      minWidth: "156px",
      borderRadius: "4px",
      padding: "6px",
      boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)",
    },
  },
  menuItem: {
    borderRadius: "4px",
    minHeight: "28px",
    padding: "4px 8px",
    "&:hover": {
      backgroundColor: "#1D74FF1A",
    },
    "&.Mui-disabled": {
      opacity: 0.5,
    },
  },
  menuItemText: {
    color: "rgba(0, 0, 0, 0.87)",
    "&:hover": {
      color: COLOR_CONSTANTS.primary.main,
    },
  },
  menuItemTextDisabled: {
    color: "rgba(0, 0, 0, 0.38)",
  },
  memoSearchCell: {
    width: "40%",
    minWidth: "15px",
    color: COLOR_CONSTANTS.text.disabled,
    ...TYPOGRAPHY.scale.caption2,
  },
  memoSearchContainer: {
    display: "flex",
    alignItems: "center",
    gap: 1,
    minWidth: 0,
  },
  memoSearchLabel: {
    ...TYPOGRAPHY.scale.caption2,
    color: COLOR_CONSTANTS.text.disabled,
    flexShrink: 0,
  },
  memoSearchInput: {
    ml: "4px",
    paddingBottom: "2px",
    minWidth: "40px",
    maxWidth: "50%",
    flex: "1 1 0",
    "& .MuiOutlinedInput-root": {
      height: "28px",
      fontSize: "12px",
      marginBottom: "3px",
      paddingRight: "8px",
      "& fieldset": {
        border: `1px solid ${COLOR_CONSTANTS.divider.divider}`,
      },
      "&:hover fieldset": {
        border: `1px solid ${COLOR_CONSTANTS.divider.divider}`,
      },
      "&.Mui-focused fieldset": {
        border: `1px solid ${COLOR_CONSTANTS.primary.main}`,
      },
    },
    "& .MuiInputBase-input": {
      padding: "6px 8px 3px",
      fontSize: "12px",
    },
  },
  memoSearchAdornment: {
    marginLeft: 0,
    paddingLeft: 0,
    paddingRight: 0,
  },
  memoSearchIcon: {
    fontSize: 16,
  },
  emptySearchResult: {
    textAlign: "center",
    height: "65px",
    color: COLOR_CONSTANTS.text.disabled,
    ...TYPOGRAPHY.scale.body2,
  },
};

export default ProjectTable;
